// File: /ts/Statistics/Statistics.tsx

import { useEffect, useState, useRef } from "react"
import { Props_Statistics } from "./Statistics.props"
import { motion, useInView, useAnimationControls } from "framer-motion"
import { Legacy_CompileMarkdown } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler'

export const Statistics = (props: Props_Statistics) => {
    const { heading, repeatable_statistic } = props;
    const sectionRef = useRef(null);
    const isInView = useInView(sectionRef, { once: true, amount: 0.3 });
    const [hasAnimated, setHasAnimated] = useState(false);

    // Find the highest number to normalize animation duration
    const highestNumber = Math.max(
        ...repeatable_statistic.map(stat => parseInt(stat.number?.value || "0", 10))
    );

    useEffect(() => {
        if (isInView && !hasAnimated) {
            setHasAnimated(true);
        }
    }, [isInView]);

    return (
        <section className="attrax-statistics" ref={sectionRef}>
            <div className="attrax-statistics__container">
                {heading.text?.value && (
                    <h2 className={`attrax-statistics__heading ${heading.hideHeading?.value === 'hide' ? 'accessible-heading' : ''}`}>
                        {heading.text.value}
                    </h2>
                )}

                <div className="attrax-statistics__grid">
                    {repeatable_statistic.map((statistic, index) => {
                        const numberValue = parseInt(statistic.number?.value || "0", 10);

                        return (
                            <div className="attrax-statistics__card" key={index}>
                                <h3 className="attrax-statistics__number">
                                    {statistic.prefix?.value}
                                    <motion.span
                                        initial={{ opacity: 1 }}
                                        animate={hasAnimated ? { opacity: 1 } : { opacity: 0 }}
                                    >
                                        {hasAnimated ? (
                                            <CountUp
                                                end={numberValue}
                                                duration={2}
                                                /* Scale duration based on highest number to ensure 
                                                   all animations end at the same time */
                                                durationScale={highestNumber > 0 ? numberValue / highestNumber : 1}
                                            />
                                        ) : 0}
                                    </motion.span>
                                    {statistic.suffix?.value}
                                </h3>

                                <h4 className="attrax-statistics__subheading">
                                    {statistic.subheading?.value}
                                </h4>

                                {statistic.text?.value && (
                                    <div className="attrax-statistics__text">
                                        {Legacy_CompileMarkdown(statistic.text.value)}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

// CountUp component to handle the animation of numbers
const CountUp = ({ end, duration = 2, durationScale = 1 }) => {
    const controls = useAnimationControls();
    const [count, setCount] = useState(0);
    const adjustedDuration = Math.max(duration * durationScale, 0.5);

    useEffect(() => {
        let startTime;
        let animationFrame;

        const updateCount = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / (adjustedDuration * 1000), 1);

            setCount(Math.floor(progress * end));

            if (progress < 1) {
                animationFrame = requestAnimationFrame(updateCount);
            }
        };

        animationFrame = requestAnimationFrame(updateCount);

        return () => {
            cancelAnimationFrame(animationFrame);
        };
    }, [end, adjustedDuration]);

    return <>{count}</>;
};